import React, { useContext, useEffect, useState } from 'react'
import { LegacyPublisherSearch } from '@App/stores/search/models/publisher-search'
import { StoresContext } from '@App/contexts'
import { PublisherFormType } from '@App/enum/publisher-form-type'
import RootStore from '@App/stores/root-store'
import './index.scss'
import CreatePublisherDetails from '@Components/WorkRegistrationSubs/PublisherFormSteps/PublisherCreateForm/CreatePublisherDetails'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    addAdministratorFormFields,
    addPublisherFormFields,
    addPublisherSchema,
    AddPublisherType,
} from '@Entities/schemas/wizardFlow/add-publisher-schema'
import SubmitButton from '@Fields/SubmitButton'
import ModalPortal from '@Components/Popups/ModalPortal'
import IsNonRepresentativePopup from '@Components/Popups/IsNonRepresentativePopup'
import RepresentativeDetails from '@Components/WorkRegistrationSubs/PublisherFormSteps/PublisherCreateForm/RepresentativeDetails'
import { PublisherSideBarState } from '@App/enum/publisher-side-bar-state'
import { PublisherModel } from '@Entities/work/publisher-model'

interface PublisherCreateFormProps<T> {
    publisherData: T
    topClass: string
    isAdministrator: boolean
    setPublisherData: (publisherData: T) => void
    addPublisherToDraft: () => void
}

const PublisherCreateForm = <T extends PublisherModel>(props: PublisherCreateFormProps<T>) => {
    const { publisherStepStore, publisherSearchStore, popupStore } = useContext<RootStore>(StoresContext)
    const { publisherData, topClass, isAdministrator, setPublisherData, addPublisherToDraft } = props
    const [step, setStep] = useState<number>(0)
    const [isRepresentative, setIsRepresentative] = useState(true)
    const [isNonRepresentativePopupOpen, setIsNonRepresentativePopupOpen] = useState<boolean>(false)

    const publisherLabel = isAdministrator ? 'Administrator' : 'Publisher'

    // if creating an Admin publisher, then admin publisher data is required
    if (isAdministrator && !publisherData) {
        throw new Error('No Administrator Publisher Data')
    }

    // if creating a nonAdmin then ogPublisherData is required
    if (!isAdministrator && !publisherData) {
        throw new Error('No Original Publisher Data')
    }

    const methods = useForm<AddPublisherType>({
        mode: 'onChange',
        criteriaMode: 'all',
        resolver: zodResolver(addPublisherSchema),
    })

    const setRepresentation = (id: number) => {
        setIsRepresentative(id === 1)
    }

    const closeNonRepresentativePopup = () => {
        setIsNonRepresentativePopupOpen(false)
        popupStore.setPublisherSidebarState(PublisherSideBarState.empty)
    }

    const checkCreatingPublisher = async (data: AddPublisherType) => {
        if (step === 0 && !isAdministrator) {
            setStep(1)
            return
        }
        const formData: T = {
            ...publisherData,
            publisherName: data.publisherName.trim(),
            ipiNumber: data.ipiNumber.trim(),
        }

        if (!isRepresentative && !isAdministrator) {
            setIsNonRepresentativePopupOpen(true)
        } else {
            if (formData) {
                setPublisherData(formData)
            }
            const searchObj: LegacyPublisherSearch = {
                publisherName: formData?.publisherName,
                publisherIpi: formData?.ipiNumber,
            }
            publisherSearchStore.setPublisherSearch(searchObj)
            await publisherSearchStore.searchPublishers()

            const searchResult = publisherSearchStore.publisherSearchModel
            if (searchResult && searchResult.results.length) {
                publisherStepStore.setFormType(PublisherFormType.afterFind)
                return
            }
            addPublisherToDraft()
        }
    }

    useEffect(() => {
        methods.reset({
            publisherName: publisherData.publisherName,
            ipiNumber: publisherData.ipiNumber ?? undefined,
        })
    }, [])

    const { publisherName, ipiNumber } = isAdministrator ? addAdministratorFormFields : addPublisherFormFields

    return (
        <FormProvider {...methods}>
            <ModalPortal isOpen={isNonRepresentativePopupOpen}>
                <IsNonRepresentativePopup onClose={() => closeNonRepresentativePopup()} />
            </ModalPortal>
            <form onSubmit={methods.handleSubmit(checkCreatingPublisher)}>
                <div className={topClass}>
                    <h2>Create {publisherLabel}</h2>
                    {step === 0 && (
                        <CreatePublisherDetails
                            publisherName={publisherName}
                            ipiNumber={ipiNumber}
                            isAdministrator={isAdministrator}
                        />
                    )}
                    {!isAdministrator && step === 1 && <RepresentativeDetails setRepresentation={setRepresentation} />}

                    <div className="create-publisher-button-container">
                        <SubmitButton label={`Create ${publisherLabel}`} disabled={!methods.formState.isValid} />
                    </div>
                </div>
            </form>
        </FormProvider>
    )
}

export default PublisherCreateForm
